import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { UsersService } from '../../Services/Intune/users.service';
import { httpResource } from '@angular/common/http';
import { IndexQueryParams, IndexResponse } from '../../Interfaces/api/Intune/users.interface';
import { SortDirection } from '@angular/material/sort';
import { DEFAULT_ORDER_BY, DEFAULT_ORDER_DIRECTION, DEFAULT_TOP, USER_SELECT_FIELDS } from '../../Consts/Intune/list-users.const';
import { IntuneUser } from '../../Models/Intune/intune-user.model';
import { NextService } from '../../Services/Intune/next.service';
import { firstValueFrom } from 'rxjs';
import { ResourceStateService } from '../../Modules/Shared/Services/resource-state.service';

@Injectable()
export class ListUsersFacade {
    private readonly _resourceState = inject(ResourceStateService);

    private readonly _usersService = inject(UsersService);
    private readonly _nextService = inject(NextService);

    private readonly _searchTerm = signal('');
    private readonly _orderBy = signal(DEFAULT_ORDER_BY);
    private readonly _order = signal<SortDirection>(DEFAULT_ORDER_DIRECTION);
    private readonly _isLoadingMore = signal(false);

    private readonly _extraUsers = signal<IntuneUser[]>([]);
    private readonly _nextLink = signal<string | null>(null);
    private readonly _count = signal<number | null>(null);

    private readonly _baseResource = httpResource<IndexResponse>(() => {
        const queryParams: IndexQueryParams = {
            search: this._searchTerm(),
            orderBy: this._orderBy(),
            order: this._order() as 'asc' | 'desc',
            top: DEFAULT_TOP,
            select: USER_SELECT_FIELDS,
        };
        return this._usersService.index(queryParams);
    });


    constructor() {
        this.initialize();
    }

    private initialize() {
        effect(() => {
            if (!this._baseResource.hasValue()) return;
            const value = this._resourceState.valueOf(this._baseResource);
            if (value?.data) {
                this._nextLink.set(value.data.next ?? null);
                this._count.set(value.data.count ?? null);
                this._extraUsers.set([]);
            }
        });
    }

    // ACTIONS
    public setSearchTerm(term: string) {
        this._searchTerm.set(term ?? '');
    }

    public setOrderBy(orderBy: string) {
        this._orderBy.set(orderBy ?? DEFAULT_ORDER_BY);
    }

    public setOrder(order: SortDirection) {
        this._order.set(order ?? DEFAULT_ORDER_DIRECTION);
    }

    public setSort(orderBy: string, order: SortDirection) {
        this.setOrderBy(orderBy);
        this.setOrder(order);
    }

    public async loadMore() {
        const next = this._nextLink();
        if (!next || this._isLoadingMore()) return;

        this._isLoadingMore.set(true);

        try {
            const response = await firstValueFrom(
                this._nextService.getNext<IntuneUser>(next)
            );

            const newUsers = response.data.value.map((u) => new IntuneUser(u));
            this._extraUsers.update((current) => [...current, ...newUsers]);

            this._nextLink.set(response.data.next ?? null);
            this._count.set(response.data.count ?? null);
        } finally {
            this._isLoadingMore.set(false);
        }
    }

    public reload() {
        this._nextLink.set(null);
        this._count.set(null);
        this._extraUsers.set([]);
        this._baseResource.reload();
    }

    public clear() {
        this._nextLink.set(null);
        this._count.set(null);
        this._extraUsers.set([]);
        this._searchTerm.set('');
    }

    // PUBLIC READ MODELS
    readonly users = computed(() => {
        const baseUsers = this._baseResource.hasValue()
            ? (this._resourceState.valueOf(this._baseResource)?.data.value.map((u) => new IntuneUser(u)) ?? [])
            : [];
        return [...baseUsers, ...this._extraUsers()];
    });
    readonly searchTerm = computed(() => this._searchTerm());
    readonly orderBy = computed(() => this._orderBy());
    readonly order = computed(() => this._order() as SortDirection);

    readonly isLoading = computed(() => this._baseResource.isLoading());
    readonly isLoadingMore = computed(() => this._isLoadingMore());
    readonly hasMore = computed(() => !!this._nextLink());
    readonly count = computed(() => this._count());
    readonly error = computed(() => this._baseResource.error());
    readonly status = computed(() => this._baseResource.status());
}